// Fixed-asset detail: the item's facts plus its write-down display — what
// it cost, what's been written down, what's left, and the settings that
// drive the schedule (method, working life, leftover value). Only posted
// write-downs render here; nothing is projected or invented.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import { Link, useParams } from "@tanstack/react-router";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { AccountPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import {
  addCwipCost,
  capitalizeFixedAsset,
  depreciateFixedAsset,
  disposeFixedAsset,
  type FixedAsset,
  getFixedAsset,
  impairFixedAsset,
  listCwipCosts,
  revalueFixedAsset,
  rollbackFixedAssetDepreciation,
} from "@/lib/api";
import { humanizeLabel, useAccountLabels } from "@/lib/labels";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "in_service":
    case "active":
      return "info";
    case "fully_depreciated":
      return "success";
    case "disposed":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: "USD" });
}

/** The worker returns a posted-journal id beside the record it touched;
 * extract it without trusting the `unknown` response slot. */
function journalEntryIdOf(value: unknown): string | undefined {
  if (value === null || typeof value !== "object") return undefined;
  const id = (value as { journal_entry_id?: unknown }).journal_entry_id;
  return typeof id === "string" && id.length > 0 ? id : undefined;
}

function accountLabel(labels: Map<string, string>, id: string): ReactNode {
  const label = labels.get(id);
  if (label) return label;
  return (
    <span className="text-[var(--muted-foreground)]" title={id}>
      Unknown account
    </span>
  );
}

function ConfirmRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-[var(--muted-foreground)]">{label}</dt>
      <dd className="text-end font-medium">{children}</dd>
    </div>
  );
}

type DisposeForm = {
  date: string;
  disposal_amount_cents: string;
  proceeds_account_id: string;
  gain_account_id: string;
  loss_account_id: string;
};

type AmountForm = { date: string; amount_cents: string; notes: string };

type ActionKind =
  | "capitalize"
  | "depreciate"
  | "rollback"
  | "revalue"
  | "impair"
  | "dispose";

interface Confirmation {
  title: string;
  description: string;
  confirmLabel: string;
  details: ReactNode;
}

function centsFromInput(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

function AssetActions({
  asset,
  onChanged,
}: {
  asset: FixedAsset;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<ActionKind | null>(null);
  const [notice, setNotice] = useState<{
    message: string;
    journalEntryId?: string;
  } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [open, setOpen] = useState<"revalue" | "impair" | "dispose" | null>(
    null,
  );
  const [confirming, setConfirming] = useState<ActionKind | null>(null);
  const accountLabels = useAccountLabels();
  const [revalueForm, setRevalueForm] = useState<AmountForm>({
    date: todayIso(),
    amount_cents: "",
    notes: "",
  });
  const [impairForm, setImpairForm] = useState<AmountForm>({
    date: todayIso(),
    amount_cents: "",
    notes: "",
  });
  const [disposeForm, setDisposeForm] = useState<DisposeForm>({
    date: todayIso(),
    disposal_amount_cents: "",
    proceeds_account_id: "",
    gain_account_id: "",
    loss_account_id: "",
  });

  const depreciable = Math.max(
    asset.purchase_price_cents - asset.residual_value_cents,
    0,
  );
  const leftToWriteDown = Math.max(
    depreciable - asset.accumulated_depreciation_cents,
    0,
  );

  const execute = async (action: ActionKind) => {
    setBusy(action);
    setActionError(null);
    setNotice(null);
    try {
      if (action === "capitalize") {
        const result = await capitalizeFixedAsset(asset.id);
        setNotice({
          message: "Capitalized — the asset is now in service.",
          journalEntryId: result.capitalization_journal_post?.journal_entry_ids?.[0],
        });
      } else if (action === "depreciate") {
        const result = await depreciateFixedAsset(asset.id);
        setNotice({
          message: "Depreciation posted for this period.",
          journalEntryId: journalEntryIdOf(result.entry),
        });
      } else if (action === "rollback") {
        const result = await rollbackFixedAssetDepreciation(asset.id);
        setNotice({
          message: "Last depreciation entry rolled back.",
          journalEntryId: journalEntryIdOf(result.entry),
        });
      } else if (action === "revalue") {
        const result = await revalueFixedAsset(asset.id, {
          date: revalueForm.date,
          revalued_amount_cents: centsFromInput(revalueForm.amount_cents),
          notes: revalueForm.notes || undefined,
        });
        setNotice({
          message: "Revaluation posted.",
          journalEntryId: journalEntryIdOf(result.revaluation),
        });
      } else if (action === "impair") {
        const result = await impairFixedAsset(asset.id, {
          date: impairForm.date,
          impairment_amount_cents: centsFromInput(impairForm.amount_cents),
          notes: impairForm.notes || undefined,
        });
        // The spec names the response slot `impairment` while the running
        // worker returns `revaluation`; accept either so the link survives.
        const raw = result as unknown as {
          impairment?: unknown;
          revaluation?: unknown;
        };
        setNotice({
          message: "Impairment posted.",
          journalEntryId: journalEntryIdOf(raw.impairment ?? raw.revaluation),
        });
      } else {
        await disposeFixedAsset(asset.id, {
          date: disposeForm.date,
          disposal_amount_cents: centsFromInput(
            disposeForm.disposal_amount_cents,
          ),
          proceeds_account_id: disposeForm.proceeds_account_id || undefined,
          gain_account_id: disposeForm.gain_account_id || undefined,
          loss_account_id: disposeForm.loss_account_id || undefined,
        });
        setNotice({
          message: "Asset disposed — it is no longer on the books.",
        });
      }
      setConfirming(null);
      setOpen(null);
      onChanged();
    } catch (cause) {
      setActionError(toErrorMessage(cause));
      setConfirming(null);
    } finally {
      setBusy(null);
    }
  };

  const buildConfirmation = (action: ActionKind): Confirmation => {
    switch (action) {
      case "capitalize":
        return {
          title: `Capitalize ${asset.name}?`,
          description:
            "Ends the build-up and puts the asset into service today. After this, no further build-up costs can be added.",
          confirmLabel: "Capitalize asset",
          details: (
            <ConfirmRow label="Date">
              {new Date().toLocaleDateString()}
            </ConfirmRow>
          ),
        };
      case "depreciate":
        return {
          title: `Depreciate ${asset.name} for this period?`,
          description:
            "Posts one period of write-down now, using the asset's method and remaining life. Nothing posts until you confirm.",
          confirmLabel: "Post depreciation",
          details: (
            <>
              <ConfirmRow label="Date">
                {new Date().toLocaleDateString()}
              </ConfirmRow>
              <ConfirmRow label="Left to write down">
                {money(leftToWriteDown)}
              </ConfirmRow>
            </>
          ),
        };
      case "rollback":
        return {
          title: `Roll back the last depreciation on ${asset.name}?`,
          description:
            "Rolls back the most recent depreciation entry and restores the book value it added. If its journal is already posted, reverse that journal first; posted depreciation cannot be rolled back directly.",
          confirmLabel: "Roll back depreciation",
          details: (
            <ConfirmRow label="Date">
              {new Date().toLocaleDateString()}
            </ConfirmRow>
          ),
        };
      case "revalue": {
        const amount = centsFromInput(revalueForm.amount_cents);
        return {
          title: `Revalue ${asset.name} to ${money(amount)}?`,
          description:
            "Posts a revaluation dated below that increases the carrying amount. Any surplus is tracked separately.",
          confirmLabel: "Post revaluation",
          details: (
            <>
              <ConfirmRow label="Date">
                {new Date(revalueForm.date).toLocaleDateString()}
              </ConfirmRow>
              <ConfirmRow label="New value">{money(amount)}</ConfirmRow>
            </>
          ),
        };
      }
      case "impair": {
        const amount = centsFromInput(impairForm.amount_cents);
        return {
          title: `Impair ${asset.name} by ${money(amount)}?`,
          description:
            "Posts an impairment dated below that decreases the carrying amount. An impairment draws down any revaluation surplus first.",
          confirmLabel: "Post impairment",
          details: (
            <>
              <ConfirmRow label="Date">
                {new Date(impairForm.date).toLocaleDateString()}
              </ConfirmRow>
              <ConfirmRow label="Impairment amount">
                {money(amount)}
              </ConfirmRow>
            </>
          ),
        };
      }
      case "dispose": {
        const proceeds = centsFromInput(disposeForm.disposal_amount_cents);
        return {
          title: `Dispose ${asset.name} for ${money(proceeds)}?`,
          description:
            "Marks the asset disposed on the date below and posts the disposal. Disposal is final — the asset can no longer be depreciated, revalued, or impaired.",
          confirmLabel: "Post disposal",
          details: (
            <>
              <ConfirmRow label="Date">
                {new Date(disposeForm.date).toLocaleDateString()}
              </ConfirmRow>
              <ConfirmRow label="Proceeds">{money(proceeds)}</ConfirmRow>
              {disposeForm.proceeds_account_id ? (
                <ConfirmRow label="Proceeds account">
                  {accountLabel(accountLabels, disposeForm.proceeds_account_id)}
                </ConfirmRow>
              ) : null}
              {disposeForm.gain_account_id ? (
                <ConfirmRow label="Gain account">
                  {accountLabel(accountLabels, disposeForm.gain_account_id)}
                </ConfirmRow>
              ) : null}
              {disposeForm.loss_account_id ? (
                <ConfirmRow label="Loss account">
                  {accountLabel(accountLabels, disposeForm.loss_account_id)}
                </ConfirmRow>
              ) : null}
            </>
          ),
        };
      }
    }
  };

  const submitAction =
    (action: "revalue" | "impair" | "dispose") =>
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const form =
        action === "revalue"
          ? revalueForm
          : action === "impair"
            ? impairForm
            : disposeForm;
      if (!form.date) {
        setActionError("Pick the date for this action first.");
        return;
      }
      if (action !== "dispose") {
        const amount = Number(
          (form as AmountForm).amount_cents.trim(),
        );
        if (!Number.isFinite(amount) || amount <= 0) {
          setActionError(
            action === "revalue"
              ? "New value must be a positive amount."
              : "Impairment amount must be a positive amount.",
          );
          return;
        }
      }
      setActionError(null);
      setConfirming(action);
    };

  if (asset.status.toLowerCase() === "disposed") {
    return null;
  }

  const confirmation = confirming ? buildConfirmation(confirming) : null;

  return (
    <section aria-label="Actions" className="surface grid gap-3 rounded-2xl p-5">
      <h2 className="text-lg font-semibold tracking-tight">Actions</h2>
      {actionError && open === null ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {actionError}
        </p>
      ) : null}
      {notice ? (
        <output className="block rounded-xl border border-[var(--success)] p-3 text-sm text-[var(--success)]">
          {notice.message}
          {notice.journalEntryId ? (
            <>
              {" "}
              <Link
                to="/journal/$entryId"
                params={{ entryId: notice.journalEntryId }}
                className="underline underline-offset-4"
              >
                View posted entry →
              </Link>
            </>
          ) : null}
        </output>
      ) : null}

      {asset.is_cwip ? (
        <Button
          type="button"
          disabled={busy !== null}
          onClick={() => setConfirming("capitalize")}
        >
          {busy === "capitalize" ? "Capitalizing…" : "Finish building (capitalize)"}
        </Button>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={busy !== null}
            onClick={() => setConfirming("depreciate")}
          >
            {busy === "depreciate" ? "Posting…" : "Depreciate one period"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={busy !== null}
            onClick={() => setConfirming("rollback")}
          >
            {busy === "rollback" ? "Rolling back…" : "Roll back last depreciation"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={busy !== null}
            onClick={() => setOpen(open === "revalue" ? null : "revalue")}
          >
            Revalue
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={busy !== null}
            onClick={() => setOpen(open === "impair" ? null : "impair")}
          >
            Impair
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={busy !== null}
            onClick={() => setOpen(open === "dispose" ? null : "dispose")}
          >
            Dispose
          </Button>
        </div>
      )}

      <Drawer
        open={open !== null && confirming === null}
        onOpenChange={(next) => {
          if (!next && busy === null) setOpen(null);
        }}
        title={`${open === "revalue" ? "Revalue" : open === "impair" ? "Impair" : "Dispose"} ${asset.name}`}
        description="Review the date and amounts. Nothing posts until you confirm the next step."
        footer={
          <Button
            type="button"
            variant="secondary"
            onClick={() => setOpen(null)}
            disabled={busy !== null}
          >
            Back to asset
          </Button>
        }
      >
        {actionError ? (
          <p role="alert" className="mb-3 text-sm text-[var(--danger)]">
            {actionError}
          </p>
        ) : null}
        {open === "revalue" ? (
          <form className="grid max-w-sm gap-2" onSubmit={submitAction("revalue")}>
            <label htmlFor="revalue-date" className="text-xs font-medium">
              Date
            </label>
            <Input
              id="revalue-date"
              type="date"
              required
              value={revalueForm.date}
              onChange={(e) =>
                setRevalueForm((f) => ({ ...f, date: e.target.value }))
              }
            />
            <label htmlFor="revalue-amount" className="text-xs font-medium">
              New value
            </label>
            <Input
              id="revalue-amount"
              type="number"
              step="0.01"
              required
              value={revalueForm.amount_cents}
              onChange={(e) =>
                setRevalueForm((f) => ({ ...f, amount_cents: e.target.value }))
              }
            />
            <Button type="submit" disabled={busy !== null}>
              Review revaluation
            </Button>
          </form>
        ) : null}

        {open === "impair" ? (
          <form className="grid max-w-sm gap-2" onSubmit={submitAction("impair")}>
            <label htmlFor="impair-date" className="text-xs font-medium">
              Date
            </label>
            <Input
              id="impair-date"
              type="date"
              required
              value={impairForm.date}
              onChange={(e) =>
                setImpairForm((f) => ({ ...f, date: e.target.value }))
              }
            />
            <label htmlFor="impair-amount" className="text-xs font-medium">
              Impairment amount
            </label>
            <Input
              id="impair-amount"
              type="number"
              step="0.01"
              required
              value={impairForm.amount_cents}
              onChange={(e) =>
                setImpairForm((f) => ({ ...f, amount_cents: e.target.value }))
              }
            />
            <Button type="submit" disabled={busy !== null}>
              Review impairment
            </Button>
          </form>
        ) : null}

        {open === "dispose" ? (
          <form className="grid max-w-sm gap-2" onSubmit={submitAction("dispose")}>
            <label htmlFor="dispose-date" className="text-xs font-medium">
              Date
            </label>
            <Input
              id="dispose-date"
              type="date"
              required
              value={disposeForm.date}
              onChange={(e) =>
                setDisposeForm((f) => ({ ...f, date: e.target.value }))
              }
            />
            <label
              htmlFor="dispose-proceeds-amount"
              className="text-xs font-medium"
            >
              Proceeds
            </label>
            <Input
              id="dispose-proceeds-amount"
              type="number"
              step="0.01"
              required
              value={disposeForm.disposal_amount_cents}
              onChange={(e) =>
                setDisposeForm((f) => ({
                  ...f,
                  disposal_amount_cents: e.target.value,
                }))
              }
            />
            <label
              htmlFor="dispose-proceeds-account"
              className="text-xs font-medium"
            >
              Proceeds account
            </label>
            <AccountPicker
              id="dispose-proceeds-account"
              value={disposeForm.proceeds_account_id}
              onChange={(value) =>
                setDisposeForm((f) => ({ ...f, proceeds_account_id: value }))
              }
            />
            <label htmlFor="dispose-gain-account" className="text-xs font-medium">
              Gain account (if any)
            </label>
            <AccountPicker
              id="dispose-gain-account"
              value={disposeForm.gain_account_id}
              onChange={(value) =>
                setDisposeForm((f) => ({ ...f, gain_account_id: value }))
              }
            />
            <label htmlFor="dispose-loss-account" className="text-xs font-medium">
              Loss account (if any)
            </label>
            <AccountPicker
              id="dispose-loss-account"
              value={disposeForm.loss_account_id}
              onChange={(value) =>
                setDisposeForm((f) => ({ ...f, loss_account_id: value }))
              }
            />
            <Button type="submit" disabled={busy !== null}>
              Review disposal
            </Button>
          </form>
        ) : null}

      </Drawer>

      <Dialog
        open={confirming !== null}
        onOpenChange={(next) => {
          if (!next && busy === null) setConfirming(null);
        }}
        title={confirmation?.title ?? ""}
        description={confirmation?.description}
      >
        {confirmation ? (
          <>
            <dl className="grid gap-1 text-sm">{confirmation.details}</dl>
            <div className="actions-row mt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirming(null)}
                disabled={busy !== null}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void execute(confirming!)}
                disabled={busy !== null}
              >
                {busy === confirming ? "Posting…" : confirmation.confirmLabel}
              </Button>
            </div>
          </>
        ) : null}
      </Dialog>
    </section>
  );
}

interface CwipCost {
  id: string;
  date: string;
  amount_cents: number;
  description?: string;
}

function CwipCostsSection({
  asset,
  onChanged,
}: {
  asset: FixedAsset;
  onChanged: () => void;
}) {
  const [costs, setCosts] = useState<CwipCost[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState(todayIso);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const reloadCosts = useCallback(() => {
    setLoading(true);
    setError(null);
    listCwipCosts(asset.id)
      .then((rows) => setCosts(rows ?? []))
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, [asset.id]);

  useEffect(reloadCosts, [reloadCosts]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (!date) throw new Error("Pick the cost date first.");
      const amount_cents = centsFromInput(amount);
      if (amount_cents <= 0)
        throw new Error("Cost must be a positive amount.");
      await addCwipCost(asset.id, {
        date,
        amount_cents,
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      setAmount("");
      setDescription("");
      setNotice("Construction cost added to the asset.");
      reloadCosts();
      onChanged();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      aria-label="Construction costs"
      className="surface grid gap-3 rounded-2xl p-5"
    >
      <h2 className="text-lg font-semibold tracking-tight">
        Construction costs
      </h2>
      <p className="text-sm text-[var(--muted-foreground)]">
        Costs added while the asset is still being built. Capitalize the asset
        to put it into service and stop adding costs.
      </p>
      {error ? (
        <div role="alert" className="text-sm text-[var(--danger)]">
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reloadCosts}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}
      {notice ? (
        <output
          role="status"
          className="rounded-xl border border-[var(--success)] p-3 text-sm text-[var(--success)]"
        >
          {notice}
        </output>
      ) : null}
      {loading && costs === null ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading costs…</p>
      ) : (costs ?? []).length === 0 ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          No construction costs recorded yet.
        </p>
      ) : (
        <ul className="grid gap-2 text-sm">
          {(costs ?? []).map((cost) => (
            <li
              key={cost.id}
              className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--border)] pb-2 last:border-b-0 last:pb-0"
            >
              <span>
                {new Date(cost.date).toLocaleDateString()} ·{" "}
                {cost.description || "Construction cost"}
              </span>
              <span className="font-medium tabular-nums">
                {money(cost.amount_cents)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <form
        aria-label="Add a construction cost"
        className="grid gap-3 sm:grid-cols-[auto_auto_1fr_auto] sm:items-end"
        onSubmit={submit}
      >
        <label htmlFor="cwip-date" className="grid gap-1 text-xs font-medium">
          Date
          <Input
            id="cwip-date"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            disabled={saving}
            required
          />
        </label>
        <label htmlFor="cwip-amount" className="grid gap-1 text-xs font-medium">
          Amount (USD)
          <Input
            id="cwip-amount"
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            disabled={saving}
            required
          />
        </label>
        <label
          htmlFor="cwip-description"
          className="grid gap-1 text-xs font-medium"
        >
          Description (optional)
          <Input
            id="cwip-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={saving}
          />
        </label>
        <Button type="submit" disabled={saving || !amount.trim()}>
          {saving ? "Adding…" : "Add cost"}
        </Button>
      </form>
    </section>
  );
}

export function FixedAssetDetailPage() {
  const { assetId } = useParams({ strict: false }) as {
    assetId: string;
  };
  const [asset, setAsset] = useState<FixedAsset | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    if (!assetId) return;
    setError(null);
    getFixedAsset(assetId)
      .then(setAsset)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [assetId]);

  useEffect(reload, [reload]);

  // Depreciable base and progress, derived from posted figures only.
  const depreciable =
    asset != null
      ? Math.max(asset.purchase_price_cents - asset.residual_value_cents, 0)
      : 0;
  const progressPct =
    asset != null && depreciable > 0
      ? Math.min(
          Math.round(
            (asset.accumulated_depreciation_cents / depreciable) * 100,
          ),
          100,
        )
      : 0;

  const scheduleFields: Array<{
    label: string;
    hint?: string;
    value: ReactNode;
  }> =
    asset != null
      ? [
          {
            label: "Written down",
            value: `${money(asset.accumulated_depreciation_cents)} (${progressPct}%)`,
          },
          {
            label: "Left to write down",
            hint: "Depreciable base: cost minus leftover (residual) value",
            value: money(
              Math.max(depreciable - asset.accumulated_depreciation_cents, 0),
            ),
          },
          {
            label: "How it's written down",
            hint: "Depreciation method (technical term)",
            value: humanizeLabel(asset.depreciation_method),
          },
          {
            label: "Working life",
            value: `${asset.useful_life_months} ${
              asset.useful_life_months === 1 ? "month" : "months"
            }`,
          },
          {
            label: "Leftover value",
            hint: "Residual value (technical term)",
            value: money(asset.residual_value_cents),
          },
          {
            label: "Timing rule",
            hint: "Depreciation convention (technical term)",
            value: humanizeLabel(asset.convention),
          },
          ...(asset.in_service_date
            ? [
                {
                  label: "In use since",
                  value: new Date(asset.in_service_date).toLocaleDateString(),
                },
              ]
            : []),
          ...(asset.revalued_amount_cents !== undefined
            ? [
                {
                  label: "Revalued to",
                  value: money(asset.revalued_amount_cents),
                },
              ]
            : []),
        ]
      : [];

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/fixed-assets"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Fixed assets
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {asset?.name ?? assetId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {asset === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : asset ? (
        <>
          <PageHeader
            title={asset.name}
            description={
              <>
                {asset.asset_number} · bought{" "}
                {new Date(asset.purchase_date).toLocaleDateString()} ·{" "}
                <StatusDot
                  tone={statusTone(asset.status)}
                  label={humanizeLabel(plainStatus(asset.status))}
                />
                {asset.is_cwip ? " · Still being built" : ""}
              </>
            }
          />

          {asset.description ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              {asset.description}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Cost
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {money(asset.purchase_price_cents)}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p
                className="text-xs font-medium text-[var(--muted-foreground)]"
                title="Accumulated depreciation (technical term)"
              >
                Written down so far
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {money(asset.accumulated_depreciation_cents)}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p
                className="text-xs font-medium text-[var(--muted-foreground)]"
                title="Net book value (technical term)"
              >
                Still worth
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {money(asset.net_book_value_cents)}
              </p>
            </div>
          </div>

          <section aria-label="Write-down schedule" className="grid gap-3">
            <h2 className="text-lg font-semibold tracking-tight">
              Write-down schedule
            </h2>
            <div className="surface grid gap-3 rounded-2xl p-5">
              <div
                role="progressbar"
                aria-valuenow={progressPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${progressPct}% written down`}
                className="h-2 overflow-hidden rounded-full bg-[var(--muted)]"
              >
                <div
                  className="h-full rounded-full bg-[var(--action)]"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <dl
                className="hidden gap-2 text-sm sm:grid-cols-2 min-[64rem]:grid"
                data-testid="fixed-asset-schedule-table"
              >
                {scheduleFields.map((field) => (
                  <div
                    key={field.label}
                    className="flex justify-between gap-3"
                  >
                    <dt
                      className="text-[var(--muted-foreground)]"
                      title={field.hint}
                    >
                      {field.label}
                    </dt>
                    <dd className="tabular-nums">{field.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="fixed-asset-schedule-stack"
            >
              {scheduleFields.map((field) => (
                <article
                  key={field.label}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <CardField label={field.label}>
                    <span title={field.hint}>{field.value}</span>
                  </CardField>
                </article>
              ))}
            </div>
          </section>

          {asset.is_cwip ? (
            <CwipCostsSection asset={asset} onChanged={reload} />
          ) : null}

          <AssetActions asset={asset} onChanged={reload} />
        </>
      ) : null}
    </div>
  );
}
